import { ReactNode, useEffect, useState } from 'react'
import DeleteIcon from '@mui/icons-material/Delete'
import JsonCodeEditor from '../JsonCodeEditor'
import { FailureRule } from '../../types/companyApiFailureMessage'
import { Autocomplete, Box, Button, FormControl, Grid, IconButton, InputLabel, List, ListItem, ListItemText, MenuItem, Select, TextField } from '@mui/material'

type MatchType = 'always' | 'empty_content' | 'field_value' | 'status_code'

function Highlight({ children }: { children: ReactNode }) {
  return <Box component="span" sx={{ fontWeight: 600 }}>{children}</Box>
}

export function describeRule(rule: FailureRule): ReactNode {
  const message = rule.replacement?.[0]?.RESULTADO?.mensagem

  if (typeof message !== 'string') {
    return 'Regra em formato avançado (não editável aqui)'
  }

  if (!rule.match) {
    return <>Sempre → mostra: <Highlight>"{message}"</Highlight></>
  }
  if (rule.match.empty_content) {
    return <>Quando o retorno vier vazio → mostra: <Highlight>"{message}"</Highlight></>
  }
  if (rule.match.content_path && rule.match.value_in) {
    return (
      <>
        Quando <Highlight>"{rule.match.content_path.join('.')}"</Highlight> for um dos valores: <Highlight>{rule.match.value_in.join(', ')}</Highlight> → mostra: <Highlight>"{message}"</Highlight>
      </>
    )
  }
  if (rule.match.status_code_in) {
    return (
      <>
        Quando o status for um dos códigos: <Highlight>{rule.match.status_code_in.join(', ')}</Highlight> → mostra: <Highlight>"{message}"</Highlight>
      </>
    )
  }
  return 'Regra em formato avançado (não editável aqui)'
}

function insertRule(rules: FailureRule[], newRule: FailureRule): FailureRule[] {
  if (!newRule.match) {
    return [...rules, newRule]
  }

  const alwaysIndex = rules.findIndex((rule) => !rule.match)
  if (alwaysIndex === -1) {
    return [...rules, newRule]
  }

  const next = [...rules]
  next.splice(alwaysIndex, 0, newRule)
  return next
}

type FailureRuleBuilderProps = {
  rules: FailureRule[]
  onChange: (rules: FailureRule[]) => void
}

export default function FailureRuleBuilder({ rules, onChange }: FailureRuleBuilderProps) {
  const [matchType, setMatchType] = useState<MatchType>('always')
  const [contentPath, setContentPath] = useState('')
  const [valueIn, setValueIn] = useState<string[]>([])
  const [statusCodes, setStatusCodes] = useState<string[]>([])
  const [message, setMessage] = useState('')
  const [rulesText, setRulesText] = useState(() => JSON.stringify(rules, null, 2))
  const [jsonError, setJsonError] = useState<string | null>(null)

  useEffect(() => {
    setRulesText(JSON.stringify(rules, null, 2))
    setJsonError(null)
  }, [rules])

  function handleRulesTextChange(value: string) {
    setRulesText(value)

    if (value.trim().length === 0) {
      setJsonError('O campo está vazio. Deixe pelo menos "[]" (lista vazia) ou monte as regras pelos campos ao lado.')
      return
    }

    try {
      const parsed = JSON.parse(value)
      if (!Array.isArray(parsed)) {
        setJsonError("O JSON precisa ser uma lista de regras.")
        return
      }
      setJsonError(null)
      onChange(parsed)
    } catch (e: any) {
      setJsonError("JSON inválido — confira se as chaves { }, colchetes [ ] e vírgulas estão todos certinhos.")
    }
  }

  const hasAlwaysRule = rules.some((rule) => !rule.match)
  const hasEmptyContentRule = rules.some((rule) => rule.match?.empty_content)

  useEffect(() => {
    if (matchType === 'always' && hasAlwaysRule) {
      setMatchType('field_value')
    }
  }, [matchType, hasAlwaysRule])

  useEffect(() => {
    if (matchType === 'empty_content' && hasEmptyContentRule) {
      setMatchType('field_value')
    }
  }, [matchType, hasEmptyContentRule])

  function resetForm() {
    setMatchType(hasAlwaysRule ? 'field_value' : 'always')
    setContentPath('')
    setValueIn([])
    setStatusCodes([])
    setMessage('')
  }

  function removeRule(index: number) {
    onChange(rules.filter((_, i) => i !== index))
  }

  function buildMatch(): FailureRule['match'] {
    if (matchType === 'empty_content') {
      return { empty_content: true }
    }
    if (matchType === 'field_value') {
      if (!contentPath.trim() || valueIn.length === 0) return undefined
      return {
        content_path: contentPath.split('.').map((part) => part.trim()).filter(Boolean),
        value_in: valueIn,
      }
    }
    if (matchType === 'status_code') {
      const codes = statusCodes.map((code) => Number(code)).filter((code) => !Number.isNaN(code))
      return codes.length > 0 ? { status_code_in: codes } : undefined
    }
    return undefined
  }

  function addRule() {
    if (!message.trim()) return

    const match = buildMatch()
    if (matchType === 'field_value' && !match) return
    if (matchType === 'status_code' && !match) return

    const newRule: FailureRule = {
      ...(match ? { match } : {}),
      replacement: [{ RESULTADO: { mensagem: message.trim() } }],
    }

    onChange(insertRule(rules, newRule))
    resetForm()
  }

  const addDisabled =
    !message.trim() ||
    (matchType === 'field_value' && (!contentPath.trim() || valueIn.length === 0)) ||
    (matchType === 'status_code' && statusCodes.length === 0)

  return (
    <Box>
      {rules.length > 0 && (
        <List dense disablePadding sx={{ marginBottom: 2 }}>
          {rules.map((rule, index) => (
            <ListItem
              key={index}
              disablePadding
              sx={{ paddingY: 0.5 }}
              secondaryAction={
                <IconButton edge="end" aria-label="remover regra" onClick={() => removeRule(index)}>
                  <DeleteIcon />
                </IconButton>
              }
            >
              <ListItemText primary={describeRule(rule)} sx={{ paddingRight: 5 }} />
            </ListItem>
          ))}
        </List>
      )}

      <Grid container spacing={3}>
        <Grid item md={6} xs={12}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="rule-match-type-label">Condição</InputLabel>
              <Select
                labelId="rule-match-type-label"
                label="Condição"
                value={matchType}
                onChange={(e) => setMatchType(e.target.value as MatchType)}
              >
                <MenuItem value="always" disabled={hasAlwaysRule}>Sempre (regra padrão)</MenuItem>
                <MenuItem value="empty_content" disabled={hasEmptyContentRule}>Quando o retorno vier vazio</MenuItem>
                <MenuItem value="field_value">Quando um campo específico tiver um valor</MenuItem>
                <MenuItem value="status_code">Quando o código de status for</MenuItem>
              </Select>
            </FormControl>

            {matchType === 'field_value' && (
              <>
                <TextField
                  fullWidth
                  label="Caminho do campo"
                  placeholder="Ex: Decodificador.NuCdRetorno"
                  value={contentPath}
                  onChange={(e) => setContentPath(e.target.value)}
                  helperText="O caminho até o campo dentro da resposta da API, separado por pontos."
                />
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={valueIn}
                  onChange={(_, newValue) => setValueIn(newValue as string[])}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Valores aceitos"
                      helperText="Digite um valor e aperte Enter. Pode adicionar mais de um."
                    />
                  )}
                />
              </>
            )}

            {matchType === 'status_code' && (
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={statusCodes}
                onChange={(_, newValue) => setStatusCodes(newValue as string[])}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Códigos de status"
                    helperText="Digite um código (ex: 503) e aperte Enter. Pode adicionar mais de um."
                  />
                )}
              />
            )}

            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Mensagem a ser exibida"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />

            <Button variant="contained" color="primary" onClick={addRule} disabled={addDisabled}>
              Adicionar regra
            </Button>
          </Box>
        </Grid>

        <Grid item md={6} xs={12}>
          <JsonCodeEditor
            label="Estrutura JSON"
            value={rulesText}
            onChange={handleRulesTextChange}
            helperText={jsonError ?? undefined}
            error={!!jsonError}
            minHeight="80px"
          />
        </Grid>
      </Grid>
    </Box>
  )
}
